import type { Middleware } from '@reduxjs/toolkit';
import { getAvatarService } from '../../services/avatar';
import type { RootState } from '../types';
import * as avatarActions from '../slices/avatarSlice';
import { api } from '../../services/api';
import { exchangeTimingService, type AvatarSpeechType } from '../../services/exchangeTiming';

interface AvatarAction {
  type: string;
  payload?: any;
}

export const avatarMiddleware: Middleware<{}, RootState> = (store) => {
  let connectionGeneration = 0;
  let service = getAvatarService("anam");
  return (next) => (action) => {
    const { type, payload } = action as AvatarAction;

    // IMPORTANT: Always call next(action) first to update the state
    const result = next(action);

    switch (type) {
      case 'avatar/connect': {
        const { patientCase } = payload;
        const state = store.getState();

        // Don't connect if already connected or not enabled
        if (state.avatar.isConnected || state.avatar.isConnecting || !state.avatar.isEnabled) {
          return result;
        }

        service = getAvatarService(state.avatar.provider ?? "anam");
        const connectionService = service;
        const generation = ++connectionGeneration;
        console.log('Avatar connecting:', { patientCase: patientCase.title });

        const { avatarId } = connectionService.selectAvatarForCase();
        store.dispatch(avatarActions.startConnecting({ avatarId }));

        const connectAvatar = async () => {
          try {
            // The backend selects the provider avatar from saved case demographics.
            console.log('Creating avatar session...');
            const avatarSession = await api.createAvatarSession({
              conversationName: patientCase?.title ? `${patientCase.title} interview` : undefined,
              caseId: patientCase?.id,
              provider: state.avatar.provider ?? "anam",
            });

            if (generation !== connectionGeneration) {
              if (avatarSession.provider === "tavus") await api.endAvatarSession(avatarSession.conversationId, "tavus");
              return;
            }

            console.log('Initializing avatar session...');
            const success = await connectionService.initializeAvatar(
              avatarSession,
              avatarId,
              {
                onStreamReady: () => {
                  if (generation !== connectionGeneration) return;
                  console.log('Avatar stream ready');
                  store.dispatch(avatarActions.streamReady());
                  store.dispatch(avatarActions.connectSuccess());

                  // Emit custom event for video component (no MediaStream - use attach() instead)
                  window.dispatchEvent(new CustomEvent('avatar-stream-ready'));
                },
                onPlaybackBlocked: (blocked: boolean) => {
                  if (generation !== connectionGeneration) return;
                  window.dispatchEvent(new CustomEvent('avatar-playback-blocked', { detail: blocked }));
                },
                onStartTalking: (context) => {
                  if (generation !== connectionGeneration) return;
                  console.log('Avatar started talking');
                  if (context?.exchangeId) {
                    exchangeTimingService.markAvatarSpeakingStarted(context.exchangeId, context.speechType);
                  }
                  store.dispatch(avatarActions.startSpeaking());
                },
                onStopTalking: () => {
                  if (generation !== connectionGeneration) return;
                  console.log('Avatar stopped talking');
                  store.dispatch(avatarActions.stopSpeaking());
                },
                onDisconnected: () => {
                  if (generation !== connectionGeneration) return;
                  console.log('Avatar disconnected by provider');
                  store.dispatch(avatarActions.disconnect());

                  // Emit custom event
                  window.dispatchEvent(new CustomEvent('avatar-disconnected'));
                },
                onError: (error: Error) => {
                  if (generation !== connectionGeneration) return;
                  console.error('Avatar error:', error);
                  store.dispatch(avatarActions.connectFailure(error.message));
                }
              }
            );

            if (generation !== connectionGeneration) return;
            if (!success) {
              return; // The adapter already reported the specific connection failure.
            }

            console.log('Avatar connected successfully');

          } catch (error: any) {
            console.error('Failed to connect avatar:', error);
            if (generation === connectionGeneration) {
              store.dispatch(avatarActions.connectFailure(error.message));
            }
          }
        };

        connectAvatar();
        break;
      }

      case 'avatar/disconnect': {
        const generation = ++connectionGeneration;
        console.log('Avatar disconnecting...');

        // DO NOT dispatch avatarActions.disconnect() here - it would create a loop!
        // The state is already updated by the reducer via next(action)

        const disconnectAvatar = async () => {
          try {
            // Set a timeout to prevent hanging
            const disconnectPromise = service.disconnect();
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Disconnect timeout')), 5000)
            );

            await Promise.race([disconnectPromise, timeoutPromise]);

            if (generation !== connectionGeneration) return;
            // Emit custom event
            window.dispatchEvent(new CustomEvent('avatar-disconnected'));
            console.log('Avatar disconnected successfully');

          } catch (error: any) {
            if (generation !== connectionGeneration) return;
            console.error('Error disconnecting avatar:', error);
            // Even if disconnect fails, emit cleanup event
            window.dispatchEvent(new CustomEvent('avatar-disconnected'));

            // Optionally set an error in state
            store.dispatch(avatarActions.setError(`Disconnect error: ${error.message}`));
          }
        };

        return disconnectAvatar();
      }

      case 'avatar/speak': {
        const { text, exchangeId, speechType = 'patient_response' } = payload as {
          text: string;
          exchangeId?: string;
          speechType?: AvatarSpeechType;
        };
        const state = store.getState();

        console.log('Avatar speak request:', { text, speechType, isConnected: state.avatar.isConnected });

        if (state.avatar.isConnected && text.trim()) {
          exchangeTimingService.markAvatarSpeakRequested(exchangeId, speechType);

          const speakText = async () => {
            try {
              console.log('Sending text to avatar:', text);
              await service.speak(text, { exchangeId, speechType });
              console.log('Avatar speech initiated successfully');
            } catch (error: any) {
              console.error('Error speaking text:', error);
              exchangeTimingService.markAvatarSpeakFailed(exchangeId, speechType);
              store.dispatch(avatarActions.setError(error.message));
            }
          };

          speakText();
        } else {
          console.warn('Cannot speak - avatar not connected or empty text:', {
            isConnected: state.avatar.isConnected,
            hasText: !!text.trim()
          });
        }
        break;
      }

      case 'avatar/interrupt': {
        const { reason } = payload || {};
        const state = store.getState();

        console.log('🛑 Avatar interrupt action received:', {
          reason,
          isConnected: state.avatar.isConnected,
          isSpeaking: state.avatar.isSpeaking
        });

        // Include a submitted turn whose playback has not started yet
        if (state.avatar.isConnected && service.canInterrupt()) {
          const interruptAvatar = async () => {
            try {
              console.log('🛑 Executing avatar interrupt...');

              await service.interrupt();

              // Update Redux state to reflect that avatar stopped speaking
              store.dispatch(avatarActions.stopSpeaking());

              console.log('✅ Avatar successfully interrupted');

              // Optional: Add a brief system message about the interruption
              if (reason === 'user_interruption' || reason === 'user_interruption_interim') {
                // Don't add system message for user interruptions - let the interruption system handle it
                console.log('🎤 User interruption - letting interruption system handle messaging');
              }

            } catch (error: any) {
              console.error('❌ Failed to interrupt avatar:', error);

              // Force stop speaking state even if interrupt failed
              store.dispatch(avatarActions.stopSpeaking());

              // Set error in avatar state
              store.dispatch(avatarActions.setError(`Interrupt failed: ${error.message}`));
            }
          };

          interruptAvatar();
        } else {
          console.log('🛑 Avatar interrupt skipped - not speaking or not connected:', {
            isConnected: state.avatar.isConnected,
            isSpeaking: state.avatar.isSpeaking
          });
        }

        break;
      }

      case 'avatar/forceReset':
      case 'avatar/reset': {
        ++connectionGeneration;
        const resetAvatar = async () => {
          try {
            // Set a timeout to prevent hanging
            const resetPromise = service.disconnect();
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Reset timeout')), 3000)
            );

            await Promise.race([resetPromise, timeoutPromise]);
            console.log('Avatar reset successfully');

          } catch (error: any) {
            console.error('Error resetting avatar:', error);
          }
        };

        resetAvatar();
        break;
      }
    }

    // Listen for WebSocket message events that contain patient responses
    if (type === 'websocket/messageReceived') {
      const { type: messageType, data } = payload;
      const state = store.getState();

      console.log('WebSocket message received in avatar middleware:', { messageType, data });

      if (messageType === 'patient-response' && data.response) {
        console.log('Patient response detected, triggering avatar speech:', data.response);

        // Avatar should speak the patient response
        if (state.avatar.isConnected) {
          store.dispatch({
            type: 'avatar/speak',
            payload: { text: data.response, exchangeId: data.exchangeId, speechType: 'patient_response' },
          });
        } else {
          console.warn('Avatar not connected, cannot speak response');
          exchangeTimingService.completeWithoutAvatar(data.exchangeId, 'avatar_not_connected');
        }
      }
    }

    return result;
  };
};
